import React from 'react';
import { View } from 'react-native';

import { ThemedText } from '@/components/themed-text';
import { TeamMember } from '@/types/organization';
import { cn } from '@/utils/cn';

type Tone = 'success' | 'warning' | 'neutral';

const TONES: Record<Tone, { bg: string; text: string; dot: string }> = {
  success: { bg: 'bg-emerald-50', text: 'text-emerald-700', dot: 'bg-emerald-500' },
  warning: { bg: 'bg-amber-50', text: 'text-amber-700', dot: 'bg-amber-500' },
  neutral: { bg: 'bg-neutral-100', text: 'text-neutral-600', dot: 'bg-neutral-400' },
};

type StatusFields = Pick<TeamMember, 'status' | 'joinedAt' | 'role' | 'pendingAppointments'>;

export function memberStatus(member: StatusFields): {
  label: string;
  tone: Tone;
} {
  if (member.status === 'DISABLED') {
    // Horário de cliente parado na agenda de quem saiu: fica à vista até repassar.
    const pending = member.pendingAppointments ?? 0;
    return pending > 0
      ? { label: `Desativado · ${pending} para repassar`, tone: 'warning' }
      : { label: 'Desativado', tone: 'neutral' };
  }
  if (member.status === 'PAUSED_PLAN') return { label: 'Pausado pelo plano', tone: 'warning' };
  if (!member.joinedAt && member.role !== 'OWNER') {
    return { label: 'Aguardando entrar', tone: 'warning' };
  }
  return { label: 'Ativo', tone: 'success' };
}

/** Estado do membro como pill suave: fundo claro, texto escuro, ponto na cor. */
export function MemberStatusPill({ member }: { member: StatusFields }) {
  const { label, tone } = memberStatus(member);
  const t = TONES[tone];
  return (
    <View className={cn('flex-row items-center gap-1.5 self-start rounded-full px-2.5 py-1', t.bg)}>
      <View className={cn('h-1.5 w-1.5 rounded-full', t.dot)} />
      <ThemedText className={cn('font-semibold text-xs', t.text)}>{label}</ThemedText>
    </View>
  );
}
